import React from "react";
import { Link } from "react-router-dom";

/**
 * Privacy Policy Page
 *
 * Simple, honest privacy policy for BibeCheck
 */
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Back Link - Top */}
        <div className="mb-8">
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium underline inline-flex items-center gap-1"
          >
            <span>←</span> Back to BibeCheck
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            BibeCheck
          </h1>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h2>
          <p className="text-gray-600">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 border border-gray-200">
          <div className="prose prose-gray max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Commitment to Your Privacy
              </h3>
              <p className="text-gray-700 leading-relaxed">
                BibeCheck is built by a solo developer (Jeyner Gil Caga) who
                respects your privacy. This policy explains what information we
                collect, why we collect it, and how we use it. We keep it simple
                and honest.
              </p>
            </section>

            {/* What We Collect */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                What Information We Collect
              </h3>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                1. Information You Provide
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Name and Email:</strong> When you sign up for the
                  waitlist or create an account
                </li>
                <li>
                  <strong>Payment Information:</strong> Processed securely
                  through Stripe (we never store your credit card details)
                </li>
                <li>
                  <strong>Questions You Ask:</strong> The Bible questions and
                  explorations you make in BibeCheck
                </li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-800 mb-3 mt-6">
                2. Information We Automatically Collect
              </h4>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Usage Data:</strong> How you use BibeCheck (which
                  features you use, what you search for)
                </li>
                <li>
                  <strong>Device Information:</strong> Browser type, operating
                  system, IP address
                </li>
                <li>
                  <strong>Analytics:</strong> We use Firebase Analytics to
                  understand how people use BibeCheck
                </li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                How We Use Your Information
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>To Provide BibeCheck:</strong> Your questions and
                  explorations are used to generate AI-powered responses and
                  visual connections
                </li>
                <li>
                  <strong>To Improve the Product:</strong> We analyze usage
                  patterns to make BibeCheck better
                </li>
                <li>
                  <strong>To Communicate with You:</strong> Send you updates,
                  launch notifications, and respond to your questions
                </li>
                <li>
                  <strong>To Process Payments:</strong> If you're a paid
                  subscriber, we use Stripe to handle billing
                </li>
                <li>
                  <strong>To Comply with Law:</strong> If legally required
                </li>
              </ul>
            </section>

            {/* AI & Third Parties */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                AI Processing & Third-Party Services
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                BibeCheck uses AI to answer your Bible questions and create
                visual connections. Here's what that means:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>AI Services:</strong> Your questions are sent to
                  third-party AI providers to generate responses. These
                  providers have their own privacy policies and data handling
                  practices.
                </li>
                <li>
                  <strong>Firebase/Google:</strong> We use Firebase for
                  database, authentication, and analytics. Google has their own
                  privacy policy.
                </li>
                <li>
                  <strong>Stripe:</strong> Payment processing is handled by
                  Stripe. They have their own privacy policy.
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Important:</strong> Your Bible questions and
                explorations may be processed by AI services. We do not share
                your personal information (name, email, payment info) with AI
                services — only the questions you ask.
              </p>
            </section>

            {/* Your Data Rights */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Your Rights & Choices
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Access Your Data:</strong> Email us anytime to see
                  what data we have about you
                </li>
                <li>
                  <strong>Delete Your Data:</strong> You can request deletion of
                  your account and all associated data
                </li>
                <li>
                  <strong>Export Your Data:</strong> Request a copy of your
                  explorations and questions
                </li>
                <li>
                  <strong>Opt Out of Emails:</strong> Unsubscribe from our
                  emails anytime (though we'll still send important account
                  updates)
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                How We Protect Your Data
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>All data is encrypted in transit (HTTPS/SSL)</li>
                <li>
                  Database access is restricted and password-protected
                </li>
                <li>Payment data is handled entirely by Stripe (PCI compliant)</li>
                <li>
                  We use Firebase Authentication for secure login
                </li>
                <li>Regular security updates and monitoring</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Honest disclosure:</strong> BibeCheck is built by a solo
                developer. While we take security seriously, we're not a large
                company with a dedicated security team. We use industry-standard
                tools (Firebase, Stripe) to protect your data.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Children's Privacy
              </h3>
              <p className="text-gray-700 leading-relaxed">
                BibeCheck is not intended for children under 13. We do not
                knowingly collect data from children under 13. If you're a parent
                and believe your child has provided us with information, please
                contact us and we'll delete it immediately.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                How Long We Keep Your Data
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>
                  <strong>Active Accounts:</strong> We keep your data as long as
                  your account is active
                </li>
                <li>
                  <strong>Deleted Accounts:</strong> Data is deleted within 30
                  days of account deletion
                </li>
                <li>
                  <strong>Waitlist Emails:</strong> Kept until you unsubscribe
                  or we launch
                </li>
                <li>
                  <strong>Analytics Data:</strong> Anonymized usage data may be
                  retained longer for product improvement
                </li>
              </ul>
            </section>

            {/* International Users */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                International Users
              </h3>
              <p className="text-gray-700 leading-relaxed">
                BibeCheck is operated from Singapore. If you're accessing
                BibeCheck from outside Singapore, your data may be transferred to
                and processed in Singapore or other countries where our service
                providers operate (like Google/Firebase servers).
              </p>
            </section>

            {/* Changes to Policy */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Changes to This Policy
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy as BibeCheck grows. If we make
                significant changes, we'll email you at least 30 days before the
                changes take effect. The "Last Updated" date at the top will
                always show when the policy was last changed.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Questions? Contact Me
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                BibeCheck is built by a solo developer, and I read every email
                personally. If you have any questions about privacy or want to
                exercise your data rights, email me:
              </p>
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                <p className="text-gray-900 font-medium">Jeyner Gil Caga</p>
                <p className="text-gray-700">Founder, BibeCheck</p>
                <p className="text-blue-600 font-medium mt-2">
                  <a href="mailto:jeyner@bibecheck.com" className="hover:underline">
                    jeyner@bibecheck.com
                  </a>
                </p>
              </div>
            </section>

            {/* Simple Summary */}
            <section className="mt-12 p-6 bg-purple-50 rounded-lg border-l-4 border-purple-600">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Privacy Policy in Plain English
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>✅ We collect your email, name, and Bible questions</li>
                <li>✅ We use third-party AI services to answer your questions</li>
                <li>✅ Stripe handles payments (we never see your card)</li>
                <li>✅ We use Firebase for database and analytics</li>
                <li>✅ We don't sell your data to anyone</li>
                <li>✅ You can delete your account anytime</li>
                <li>✅ Email me if you have questions</li>
              </ul>
            </section>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="text-purple-600 hover:text-purple-700 font-medium underline">
            ← Back to BibeCheck
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
